import { useState, useEffect } from 'react'
import { useLocale } from '../../App'

interface GloryViewProps {
  authToken: string | null
  onBack: () => void
}

const TITLE_CONFIG: Record<string, { icon: string; gradient: string; color: string }> = {
  '扑克之神': { icon: '💎', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', color: 'text-fuchsia-300' },
  '扑克王者': { icon: '👑', gradient: 'from-amber-600 via-yellow-500 to-orange-500', color: 'text-yellow-300' },
  '扑克传奇': { icon: '🏆', gradient: 'from-orange-600 to-red-500', color: 'text-orange-300' },
  '扑克大师': { icon: '♠️', gradient: 'from-blue-600 to-indigo-500', color: 'text-blue-300' },
  '扑克精英': { icon: '♦️', gradient: 'from-emerald-600 to-teal-500', color: 'text-emerald-300' },
  '扑克尖兵': { icon: '🎯', gradient: 'from-slate-500 to-slate-400', color: 'text-slate-300' },
  '扑克新星': { icon: '🃏', gradient: 'from-amber-700 to-amber-600', color: 'text-amber-400' },
}

const TITLE_KEYS: Record<string, string> = {
  '扑克之神': 'app.title_poker_god',
  '扑克王者': 'app.title_poker_king',
  '扑克传奇': 'app.title_poker_legend',
  '扑克大师': 'app.title_poker_master',
  '扑克精英': 'app.title_poker_elite',
  '扑克尖兵': 'app.title_poker_veteran',
  '扑克新星': 'app.title_poker_rookie',
}

export function GloryView({ authToken, onBack }: GloryViewProps) {
  const { t } = useLocale()
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!authToken) return
    fetch('/api/user/glory', {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [authToken])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center" style={{ background: 'linear-gradient(180deg, #0a0e1a, #141b2d)' }}>
        <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-400 rounded-full animate-spin" />
      </div>
    )
  }

  if (!data) return null

  const titleKey = TITLE_KEYS[data.title] || 'app.title_poker_rookie'
  const tc = TITLE_CONFIG[data.title] || TITLE_CONFIG['扑克新星']
  const wr = (data.winRate * 100).toFixed(1)

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'linear-gradient(180deg, #0a0e1a, #141b2d)' }}>
      {/* Back button */}
      <div className="shrink-0 px-4 pt-3">
        <button onClick={onBack} className="text-amber-400/80 hover:text-amber-300 text-xl">{t('app.back')}</button>
      </div>

      {/* Poker card */}
      <div className="flex-1 flex items-center justify-center px-6 pb-4">
        <div
          className="w-full max-w-xs rounded-2xl overflow-hidden shadow-2xl border"
          style={{
            background: 'linear-gradient(145deg, #f8f4ec 0%, #efe8d8 100%)',
            borderColor: 'rgba(180,120,50,0.3)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(180,120,50,0.1)',
            transform: 'perspective(800px) rotateY(-2deg) rotateX(1deg)',
          }}
        >
          {/* Card corners - suit decorations */}
          <div className="px-4 pt-3 flex justify-between text-amber-700/20 text-lg font-serif">
            <span>♠</span>
            <span style={{ transform: 'rotate(180deg)' }}>♠</span>
          </div>

          {/* Avatar & name */}
          <div className="flex flex-col items-center px-6 -mt-1">
            <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-amber-500/30 shadow-lg shadow-amber-500/10 bg-gradient-to-br from-amber-200 to-amber-400 flex items-center justify-center">
              {data.photoUrl ? (
                <img src={data.photoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl">👤</span>
              )}
            </div>
            <h2 className="mt-3 text-xl font-bold text-slate-800 tracking-wide">
              {data.firstName || data.username || t('app.glory_player')}
            </h2>
            <div className="flex items-center gap-1 mt-0.5 text-xs text-amber-700/80">
              <span>♠</span>
              <span>ID: {data.tgId?.slice(-6) || '******'}</span>
              <span>♥</span>
            </div>
          </div>

          {/* Divider */}
          <div className="mx-8 my-4 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(180,120,50,0.3), transparent)' }} />

          {/* Win rate */}
          <div className="text-center px-6">
            <div className="text-4xl font-extrabold tracking-tight text-slate-800 tabular-nums">
              {wr}<span className="text-lg text-amber-700/80">%</span>
            </div>
            <div className="text-[10px] text-amber-700/70 tracking-widest mt-0.5">WIN RATE</div>
          </div>

          {/* Title badge */}
          <div className="flex justify-center mt-4 px-6">
            <div
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold shadow-lg bg-gradient-to-r ${tc.gradient} text-white`}
            >
              <span>{tc.icon}</span>
              <span>{t(titleKey)}</span>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-px mx-6 mt-5 rounded-lg overflow-hidden" style={{ background: 'rgba(180,120,50,0.1)' }}>
            {[
              { label: t('app.glory_games'), value: data.totalGames },
              { label: t('app.glory_wins'), value: data.wins },
              { label: t('app.glory_ties'), value: data.ties },
            ].map((s) => (
              <div key={s.label} className="py-2.5 text-center" style={{ background: 'rgba(255,255,255,0.5)' }}>
                <div className="text-sm font-bold text-slate-800 tabular-nums">{s.value}</div>
                <div className="text-[9px] text-amber-700/70 tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Extra stats */}
          <div className="flex justify-center gap-6 mt-4 px-6 pb-3">
            <div className="text-center">
              <div className="text-xs font-bold text-slate-700 tabular-nums">{data.maxWinStreak}</div>
              <div className="text-[8px] text-amber-700/70 tracking-wider">{t('app.glory_max_streak')}</div>
            </div>
            <div className="text-center">
              <div className="text-xs font-bold text-amber-700 tabular-nums">💰{data.totalChipsEarned.toLocaleString()}</div>
              <div className="text-[8px] text-amber-700/70 tracking-wider">{t('app.glory_total_chips')}</div>
            </div>
            <div className="text-center">
              <div className="text-xs font-bold text-slate-700 tabular-nums">{data.winStreak}</div>
              <div className="text-[8px] text-amber-700/70 tracking-wider">{t('app.glory_cur_streak')}</div>
            </div>
          </div>

          {/* Bottom corner suits */}
          <div className="px-4 pb-3 flex justify-between text-amber-700/20 text-lg font-serif" style={{ transform: 'rotate(180deg)' }}>
            <span>♥</span>
            <span style={{ transform: 'rotate(180deg)' }}>♥</span>
          </div>
        </div>
      </div>
    </div>
  )
}
